import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MAX_SUBGRAPH_ID,
  REQUEST_ID,
  STREAMS_PAGE_SIZE,
} from "@sablier/v2-constants";
import { useRequestStreamList } from "@sablier/v2-hooks";
import { _ } from "@sablier/v2-mixins";
import { type IStream, Stream } from "@sablier/v2-models";
import extensions from "~/client/extensions";
import type { StreamShape } from "@sablier/v2-constants";
import type { IFilter, ISearch } from "@sablier/v2-types";
import { supported } from "../constants";
import { useStreamStoreAccessor, useStreamStoreSearch } from "./store/stream";

const OPTIONS = {
  after: MAX_SUBGRAPH_ID,
  first: STREAMS_PAGE_SIZE,
};

export default function useStreamsSearch(
  filter: IFilter,
  isReadyExternal?: boolean,
) {
  const { search: result, set } = useStreamStoreSearch();
  const access = useStreamStoreAccessor();

  const [options, setOptions] = useState<typeof OPTIONS>(OPTIONS);

  const isEnabled = useMemo(() => {
    if (!supported.find((chain) => chain.id !== filter.chainId)) {
      return false;
    }

    if (
      _.isNilOrEmptyString(filter.chainId) &&
      _.isNilOrEmptyString(filter.recipient) &&
      _.isNilOrEmptyString(filter.sender) &&
      (_.isNil(filter.streamIds) || filter.streamIds.length === 0)
    ) {
      return false;
    }

    return isReadyExternal && !result?.options.isComplete;
  }, [filter, result, isReadyExternal]);

  const onSuccess = useCallback(
    (_result: ISearch<IStream>) => {
      const result = _.clone(_result);
      const state = access();
      const set = state.api.setSearch;
      const search = state.search;

      const info = (result: unknown, label = "new") =>
        console.info("%c[search]", "color: cadetblue", `[${label}]`, {
          result,
        });

      result.streams.forEach((stream) => {
        const resolved = Stream.findShape(extensions.identify(stream));
        if (!resolved.isUnknown) {
          stream.shape = resolved.id as StreamShape;
        }
      });

      /** If there are no results, you can set initial ones care-free */
      if (_.isNil(search)) {
        set(result);
        info(result);
      } else {
        /** If filters changed, it meas we requested for a new set of streams. Override based on these new rules. */
        if (!_.isEqual(search.filter, result.filter)) {
          set(result);
          info(result);
        } else if (!_.isEqual(search.options, result.options)) {
          /** If filters remain the same, options may have changed (e.g. pagination requested followup items). Merge results. */
          const value = _.clone(search);
          value.options = result.options;
          value.streams = [...value.streams, ...result.streams];
          set(value);
          info(value.streams, "more");
        }
      }
    },
    [access],
  );

  const onError = useCallback(
    (_error: unknown, result: ISearch<IStream>) => {
      set(result);
    },
    [set],
  );

  useEffect(() => {
    setOptions(OPTIONS);
  }, [filter]);

  const { isLoading, error } = useRequestStreamList({
    key: REQUEST_ID.streamListSearch,
    filter,
    isEnabled,
    onError,
    onSuccess,
    options,
  });

  const doMore = useCallback(() => {
    if (!isLoading && _.isNilOrEmptyString(error)) {
      setOptions((prev) => ({
        ...prev,
        after: result?.options.after || OPTIONS.after,
      }));
    }
  }, [error, isLoading, setOptions, result]);

  return {
    doMore,
    error,
    isLoading,
    result,
  };
}
